import {Injectable, NgZone} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {PushPayload} from "cordova-plugin-firebase-messaging/types/FirebaseMessaging";
type FirebaseMessaging = typeof import("cordova-plugin-firebase-messaging/types/FirebaseMessaging")

import {ZoneObservable} from '../cordova/zone-observable';
import {CordovaService, PLATFORM} from '../cordova/cordova.service';

export type PushInfo = {
  title: string;
  body: string;
  page?: string;
};

@Injectable({
  providedIn: 'root'
})
export class FcmService {

  // tslint:disable-next-line:variable-name
  private messaging: FirebaseMessaging;

  constructor(
    private zone: NgZone,
    private cordovaSvc: CordovaService,
  ) {
    this.cordovaSvc.deviceready.subscribe(() => {
      const plugins: CordovaPlugins = this.cordovaSvc.cordova?.plugins;
      if (plugins?.firebase?.messaging) {
        this.messaging = plugins.firebase.messaging;
      }
      else {
        console.error('FcmService::constructor() cordova.plugins is null!');
      }
    });
  }

  private isInit(): boolean {
    return !!this.cordovaSvc.cordova?.plugins?.firebase?.messaging;
  }


  /**
   * Clear all notifications from system notification bar.
   */
  public clearNotifications(): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::clearNotifications() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone, (observer) => {
      this.messaging.clearNotifications().then(() => {
        observer.next();
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }


  /**
   * Delete the Instance ID (Token) and the data associated with it.
   * Call getToken to generate a new one.
   */
  public deleteToken(): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::deleteToken() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone, (observer) => {
      this.messaging.deleteToken().then(() => {
        observer.next();
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  public getToken(): Observable<string> {
    if (!this.isInit()) {
      console.error('FcmService::getToken() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<string>(this.zone, (observer) => {
      this.messaging.getToken().then(token => {
        console.log('FcmService::getToken() token =', token);
        observer.next(token);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  /**
   * Registers callback to notify when FCM token is updated.
   */
  public onTokenRefresh(): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::onTokenRefresh() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone, (observer) => {
      this.messaging.onTokenRefresh(() => {
        observer.next();
      },
      (error: string) => {
        console.error('FcmService::onTokenRefresh() error =', error);
        observer.error(error);
      });
    });
  }

  /**
   * 앱이 백그라운드에서 실행되고 있거나 완전히 종료된 상태일 때 백그라운드 푸시 메시지를 처리
   */
  public onBackgroundMessage(): Observable<PushPayload> {
    if (!this.isInit()) {
      console.error('FcmService::onBackgroundMessage() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<PushPayload>(this.zone,
      (observer) => {
        this.messaging.onBackgroundMessage((payload: PushPayload) => {
            console.log('FcmService::onBackgroundMessage() payload =', payload);
            observer.next(payload);
          },
          (error: string) => {
            console.error('FcmService::onBackgroundMessage() error =', error);
            observer.error(error);
          });
      });
  }

  /**
   * 앱이 foreground에서 작동하고 있을 때 푸시를 누르면 처리하는 함수.
   */
  public onMessage(): Observable<PushInfo> {
    if (!this.isInit()) {
      console.error('FcmService::onNotification() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<PushInfo>(this.zone,
      (observer) => {
        this.messaging.onMessage((payload: PushPayload) => {
            console.log('FcmService::onMessage() payload =', payload);

            const page = payload['page'];
            const {title, body} = this.cordovaSvc.isAndroid ? payload.gcm : payload.aps?.alert;
            const pushInfo: PushInfo = {
              title,
              body,
              ...(page ? {page} : {}),
            };

            observer.next(pushInfo);
          },
          (error: string) => {
            console.error('FcmService::onMessage() error =', error);
            observer.error(error);
          });
      });
  }

  public requestPermission(forceShow: boolean = false): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::requestPermission() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone,
      (observer) => {
        this.messaging.requestPermission({forceShow}).then(() => {
          // console.warn('FcmService::requestPermission() success');
          observer.next();
          observer.complete();
        }).catch(error => {
          console.error('FcmService::requestPermission() error =', error);
          observer.error(error);
        });
      });
  }

  public subscribe(topic: string): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::subscribe() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone,
      (observer) => {
        this.messaging.subscribe(topic).then(() => {
          console.log(`FcmService::subscribe(${topic}) success`);
          observer.next();
          observer.complete();
        }).catch(error => {
          console.error(`FcmService::subscribe(${topic}) error =`, error);
          observer.error(error);
        });
      });
  }

  public unsubscribe(topic: string): Observable<void> {
    if (!this.isInit()) {
      console.error('FcmService::unsubscribe() should be called after cordova inited!');
      return throwError({detail: 'cordova has not been initialized!'});
    }

    return ZoneObservable.create<void>(this.zone,
      (observer) => {
        this.messaging.unsubscribe(topic).then(() => {
          console.log(`FcmService::unsubscribe(${topic}) success`);
          observer.next();
          observer.complete();
        }).catch(error => {
          console.error(`FcmService::unsubscribe(${topic}) error =`, error);
          observer.error(error);
        });
      });
  }
}
